import { Link, useLocation } from 'react-router';
import {
  Folder,
  Users,
  Settings,
  BarChart3,
  Link2,
  ShieldCheck,
  LogOut,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: BarChart3 },
  { name: 'Folders', href: '/folders', icon: Folder },
  { name: 'Share Links', href: '/share-links', icon: Link2 },
];

const adminNavigation = [
  { name: 'Users', href: '/admin/users', icon: Users },
  { name: 'Audit Logs', href: '/admin/audit', icon: ShieldCheck },
  { name: 'Settings', href: '/admin/settings', icon: Settings },
];

export function Sidebar() {
  const location = useLocation();
  const { user, tenant, logout } = useAuth();

  const primaryColor = tenant?.branding_primary_color || '#2563eb';

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // Generate CSS custom properties for branding
  const brandingStyles = {
    '--brand-primary': primaryColor,
    '--brand-primary-light': `${primaryColor}15`,
  } as React.CSSProperties;

  return (
    <div className="flex h-full w-64 flex-col border-r border-gray-200 bg-white" style={brandingStyles}>
      {/* Logo */}
      <div className="flex h-16 items-center px-6 border-b border-gray-200">
        <Link to="/dashboard" className="flex items-center">
          {tenant?.branding_logo_url ? (
            <img
              src={tenant.branding_logo_url}
              alt={tenant.branding_company_name || 'Logo'}
              className="h-8 w-auto"
            />
          ) : (
            <span
              className="text-xl font-bold"
              style={{ color: tenant?.branding_primary_color || '#2563eb' }}
            >
              {tenant?.branding_company_name || 'SecureSend'}
            </span>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-4">
        {navigation.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
                active
                  ? ''
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              )}
              style={active ? { backgroundColor: `${primaryColor}15`, color: primaryColor } : undefined}
            >
              <Icon
                className="mr-3 h-5 w-5 shrink-0"
                style={active ? { color: primaryColor } : { color: '#9ca3af' }}
              />
              {item.name}
            </Link>
          );
        })}

        {/* Admin section */}
        {user?.role === 'admin' && (
          <>
            <div className="pt-6">
              <p className="px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Administration
              </p>
            </div>
            {adminNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    active
                      ? ''
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  )}
                  style={active ? { backgroundColor: `${primaryColor}15`, color: primaryColor } : undefined}
                >
                  <Icon
                    className="mr-3 h-5 w-5 shrink-0"
                    style={active ? { color: primaryColor } : { color: '#9ca3af' }}
                  />
                  {item.name}
                </Link>
              );
            })}
          </>
        )}
      </nav>

      {/* User section */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex items-center">
          <div className="shrink-0">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full font-semibold"
              style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
            >
              {user?.name.charAt(0).toUpperCase()}
            </div>
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-700 truncate">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={() => logout()}
            className="ml-2 rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
            title="Sign out"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
